import { notification } from 'antd'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { makeAutoObservable } from 'mobx'

import { DATE_FORMAT_EMPTY_TIME } from 'shared/constants/values'
import { Country } from 'shared/types/types'

import { userApi } from '../../api'
import {
  BaseDocumentDTO,
  Citizenship,
  CompanyType,
  DocumentType,
  GENDER,
  GenderNumber,
  Passport,
  RequisitesData,
  UserModuleKeys,
} from '../../types'
import { RegistrationUiStore } from './RegistrationUiStore'

const getApiKey = (key: string) => `${UserModuleKeys.api}.${key}`

export class RegistrationApiStore {
  constructor(protected readonly uiStore: RegistrationUiStore) {
    makeAutoObservable(this)
  }

  tryRegisterOwner = async () => {
    const { profile, companyDetails, rootStore } = this.uiStore
    const {
      payAccount,
      bankName,
      bik,
      companyName,
      corAccount,
      companyType,
      inn,
      dateOfBirthday,
      dateOfIssue,
      departmentCode,
      documentPhoto,
      withDocumentPhoto,
      gender,
      passportIssued,
      passport,
      placeOfBirth,
      documentType,
      expirationDate,
      nationality,
      identificationNumber,
      registrationAddress,
      socialCardNumber,
      documentNumber,
      personalNumber,
      iin,
      innCitizen,
      legalAddress,
      gln,
    } = companyDetails
    const { firstName, lastName, middleName } = profile
    const citizenship = companyDetails.selectedCitizenship.value
    const genderNumber = gender === GENDER.MALE ? GenderNumber.MALE : GenderNumber.FEMALE

    this.uiStore.setRegistrationLoading(true)

    const getRequisitesData = () => {
      const requisitesData: RequisitesData = {}

      switch (companyDetails.selectedCountry.country) {
        case Country.RU:
          requisitesData.requisites_ru = {
            account_number: payAccount,
            bank_name: bankName,
            bik,
            company_name: companyName,
            correspondent_account: corAccount,
          }

          if (companyType === CompanyType.ip) {
            requisitesData.requisites_ru.ip_inn = inn
          } else {
            requisitesData.requisites_ru.ooo_inn = inn
          }

          break
        case Country.BY:
          requisitesData.requisites_by = {
            account_number: payAccount,
            bank_name: bankName,
            company_name: companyName,
            unp: inn,
          }

          if (bik.length === 8) {
            requisitesData.requisites_by.bik_8 = bik
          } else {
            requisitesData.requisites_by.bik_11 = bik
          }

          break
        case Country.KZ:
          requisitesData.requisites_kz = {
            account_number: payAccount,
            bank_name: bankName,
            company_name: companyName,
            iin_bin: inn,
          }

          if (bik.length === 8) {
            requisitesData.requisites_kz.bik_8 = bik
          } else {
            requisitesData.requisites_kz.bik_11 = bik
          }

          break
        case Country.AM:
          requisitesData.requisites_am = {
            account_number: payAccount,
            bank_name: bankName,
            company_name: companyName,
            inn: inn,
          }

          if (bik.length === 8) {
            requisitesData.requisites_am.bik_8 = bik
          } else {
            requisitesData.requisites_am.bik_11 = bik
          }

          break
        case Country.KG:
          requisitesData.requisites_kg = {
            account_number: payAccount,
            bank_name: bankName,
            company_name: companyName,
            inn: inn,
            bik,
          }

          break
        case Country.UZ:
          requisitesData.requisites_uz = {
            bik_mfo: bik,
            account_number: payAccount,
            bank_name: bankName,
            company_name: companyName,
          }

          if (companyType === CompanyType.ooo) {
            requisitesData.requisites_uz.ooo_inn = inn
          } else {
            requisitesData.requisites_uz.pinfl = inn
          }

          break
        case Country.GE:
          requisitesData.requisites_ge = {
            account_number: payAccount,
            bank_name: bankName,
            company_name: companyName,
            inn,
          }

          if (bik.length === 8) {
            requisitesData.requisites_ge.bik_8 = bik
          } else {
            requisitesData.requisites_ge.bik_11 = bik
          }

          break
        case Country.TJ:
          requisitesData.requisites_tj = {
            account_number: payAccount,
            bank_name: bankName,
            company_name: companyName,
            inn: inn,
            bik,
          }

          break
      }

      return requisitesData
    }

    const getPassportData = () => {
      const passportData: Passport = {}

      const basePassport: BaseDocumentDTO = {
        first_name: firstName,
        last_name: lastName,
        document_photo: documentPhoto,
        with_document_photo: withDocumentPhoto,
        issue_date: dayjs(dateOfIssue, 'DD.MM.YYYY').format(DATE_FORMAT_EMPTY_TIME),
        date_of_birth: dayjs(dateOfBirthday, 'DD.MM.YYYY').format(DATE_FORMAT_EMPTY_TIME),
      }

      const expiration_date = dayjs(expirationDate, 'DD.MM.YYYY').format(DATE_FORMAT_EMPTY_TIME)

      switch (citizenship) {
        case Citizenship.RU:
          passportData.passport_ru = {
            ...basePassport,
            department_code: departmentCode,
            document_photo: documentPhoto,
            last_name: lastName,
            patronymic: middleName,
            gender,
            issued_by: passportIssued,
            passport_number: `${passport.substring(0, 4)} ${passport.substring(4, 10)}`,
            place_of_birth: placeOfBirth,
            with_document_photo: withDocumentPhoto,
          }

          break
        case Citizenship.KZ:
          if (documentType === DocumentType.passport) {
            passportData.passport_kz = {
              ...basePassport,
              patronymic: middleName,
              gender,
              identification_number: identificationNumber,
              expiration_date,
              issued_by: passportIssued,
              nationality,
              passport_number: passport,
              place_of_birth: placeOfBirth,
            }
          } else {
            passportData.id_card_kz = {
              ...basePassport,
              patronymic: middleName,
              iin: iin,
              document_number: documentNumber,
              place_of_birth: placeOfBirth,
              nationality,
              expiration_date,
              issued_by: passportIssued,
              gender,
            }
          }

          break
        case Citizenship.AM:
          if (documentType === DocumentType.passport) {
            passportData.passport_am = {
              ...basePassport,
              patronymic: middleName,
              gender: genderNumber,
              expiration_date,
              department_code: departmentCode,
              passport_number: passport,
              registration_address: registrationAddress,
            }
          } else {
            passportData.id_card_am = {
              ...basePassport,
              document_number: documentNumber,
              patronymic: middleName,
              gender: genderNumber,
              expiration_date,
              department_code: departmentCode,
              social_card_number: socialCardNumber,
            }
          }

          break
        case Citizenship.BY:
          if (documentType === DocumentType.passport) {
            passportData.passport_by = {
              ...basePassport,
              gender: genderNumber,
              identification_number: identificationNumber,
              issued_by: passportIssued,
              expiration_date,
              patronymic: middleName,
              nationality,
              passport_number: passport,
              place_of_birth: placeOfBirth,
            }
          } else {
            passportData.id_card_by = {
              ...basePassport,
              citizenship,
              document_number: documentNumber,
              patronymic: middleName,
              gender: genderNumber,
              expiration_date,
              department_code: departmentCode,
              identification_number: identificationNumber,
              place_of_birth: placeOfBirth,
            }
          }

          break
        case Citizenship.UZ:
          if (documentType === DocumentType.passport) {
            passportData.passport_uz = {
              ...basePassport,
              patronymic: middleName,
              gender,
              place_of_birth: placeOfBirth,
              nationality,
              issued_by: passportIssued,
              passport_number: passport,
              expiration_date,
            }
          } else {
            passportData.id_card_uz = {
              ...basePassport,
              patronymic: middleName,
              gender: genderNumber,
              expiration_date,
              citizenship,
              document_number: documentNumber,
              personal_number: personalNumber,
              place_of_birth: placeOfBirth,
              department_code: departmentCode,
            }
          }

          break
        case Citizenship.KG:
          if (documentType === DocumentType.passport) {
            passportData.passport_kg = {
              ...basePassport,
              patronymic: middleName,
              gender,
              citizenship,
              nationality,
              department_code: departmentCode,
              personal_number: identificationNumber,
              passport_number: passport,
              expiration_date,
            }
          } else {
            passportData.id_card_kg = {
              ...basePassport,
              patronymic: middleName,
              gender: genderNumber,
              citizenship,
              document_number: documentNumber,
              expiration_date,
              department_code: departmentCode,
              personal_number: personalNumber,
              place_of_birth: placeOfBirth,
            }
          }

          break
        case Citizenship.GE:
          passportData.id_card_ge = {
            ...basePassport,
            document_number: documentNumber,
            expiration_date,
            gender: genderNumber,
            issued_by: passportIssued,
            identification_number: identificationNumber,
            place_of_birth: placeOfBirth,
          }

          break
        case Citizenship.TJ:
          passportData.id_card_tj = {
            ...basePassport,
            document_number: documentNumber,
            expiration_date,
            patronymic: middleName,
            gender: genderNumber,
            issued_by: passportIssued,
            identification_number: identificationNumber,
            place_of_birth: placeOfBirth,
            inn_citizen: innCitizen,
          }

          break
      }

      return passportData
    }

    try {
      await userApi.registerOwner({
        owner_data: {
          contact_info: {
            email: profile.email,
            telegram: profile.telegramLogin,
            snils: profile.snils,
            legal_address: legalAddress,
            gln,
            phone_number: `+${rootStore.appStore.user.info.phone.toString()}`,
            additional_phone_country: profile.additionalPhoneModel.selectedCountry.country,
            additional_phone: `+${profile.additionalPhoneModel.fullPhone.toString()}`,
          },
          activity_form: {
            country: companyDetails.selectedCountry.country,
            identity_photos: companyDetails.taxFiles,
            organization_form: companyDetails.companyType,
            organization_tax: companyDetails.taxType,
          },
          passport: getPassportData(),
          requisites_data: getRequisitesData(),
        },
      })

      notification.success({
        message: i18next.t(getApiKey('Регистрация завершена')),
        description: i18next.t(getApiKey('Необходимо выполнить повторный')),
      })

      this.uiStore.rootStore.appStore.logout()
    } catch (error) {
      notification.error({
        message: i18next.t(getApiKey('Ошибка регистрации')),
        description: (error as Error).message,
      })
      throw error
    } finally {
      this.uiStore.setRegistrationLoading(false)
    }
  }
}
